<template>
  <div class="content profit-loss-carry-forward-list">
    <div class="form-content">
      <div style="margin-bottom: 1%">
        <div style="display: flex; align-items: center">
          <a-button type="primary" @click="openCreateModal">
            <i class="icon iconfont icon-new" style="margin-right: 5px"></i>
            {{ $t('新增') }}
          </a-button>
        </div>
      </div>
      <a-row :gutter="16">
        <a-col :span="6">
          <SelectAccountSet
            :placeholder="$t('账簿')"
            :code="searchForm.accountSetUnionCode"
            @change="changeSearchAccountSet"
          />
        </a-col>
        <a-col :span="6">
          <CommonQuerySelect
            api="queryAccountingCalendarDetailPage"
            valueName="accounting_period_code"
            labelName="accounting_period_name"
            :code.sync="searchForm.accountingPeriod"
            :placeholder="$t('会计期间')"
          />
        </a-col>
        <a-col :span="6">
          <a-input allow-clear v-model.trim="searchForm.orderSn" :placeholder="$t('单据编号')" />
        </a-col>
      </a-row>
      <a-row :gutter="16" style="margin-top: 10px">
        <a-col :span="6">
          <a-button @click="queryList(1)" type="primary">
            <i class="iconfont" style="margin-right: 5px">&#xe647;</i>
            {{ $t('查询') }}
          </a-button>
          <a-button @click="resetSearch">
            <a-icon type="redo" />
            {{ $t('重置') }}
          </a-button>
        </a-col>
      </a-row>
    </div>

    <div class="status-tabs mt10">
      <a-tabs :active-key="activeStatusKey" size="small" @change="handleStatusTabChange">
        <a-tab-pane key="ALL" :tab="$t('全部')"></a-tab-pane>
        <a-tab-pane v-for="item in statusTabList" :key="item.code" :tab="item.name"></a-tab-pane>
      </a-tabs>
    </div>

    <div class="table-content mt10">
      <div class="table-body">
        <a-table
          size="small"
          :columns="columns"
          :loading="loading"
          :dataSource="tableData"
          :pagination="false"
          rowKey="id"
          :scroll="{ x: 'max-content' }"
        >
          <template slot="no" slot-scope="text, record, index">
            <span>{{ index + 1 }}</span>
          </template>

          <template slot="voucherSn" slot-scope="text, record">
            <span
              v-if="record.voucherGenerated === 1 && record.voucherSn"
              class="text-link"
              @click="viewVoucherDetail(record.voucherId, record.voucherSn)"
            >
              {{ record.voucherSn }}
            </span>
            <span v-else>-</span>
          </template>

          <template slot="reverseVoucherSn" slot-scope="text, record">
            <span
              v-if="text && record.reverseVoucherId"
              class="text-link"
              @click="viewVoucherDetail(record.reverseVoucherId, text)"
            >
              {{ text }}
            </span>
            <span v-else-if="text">{{ text }}</span>
            <span v-else>-</span>
          </template>

          <template slot="specifiedAccountCarryForward" slot-scope="text">
            <span>{{ text === 1 ? $t('是') : $t('否') }}</span>
          </template>

          <template slot="action" slot-scope="text, record">
            <a-button
              type="link"
              size="small"
              v-if="record.voucherGenerated !== 1"
              @click="handleGenerateVoucher(record)"
            >
              {{ $t('生成凭证') }}
            </a-button>
            <a-button
              v-else-if="!record.reverseVoucherSn"
              type="link"
              size="small"
              @click="confirmReverseVoucher(record)"
            >
              {{ $t('反结转') }}
            </a-button>
            <span v-else>-</span>
          </template>

          <template slot="expandedRowRender" slot-scope="record">
            <CommonTable
              style="background: #fff; padding: 10px 0"
              :dataSource="record.detailList || []"
              :columns="detailColumns"
              :showPagination="false"
              :showSetting="false"
              rowKey="id"
            ></CommonTable>
          </template>
        </a-table>
      </div>
      <div class="table-footer mt10">
        <pagination
          :pageNo="pageNo"
          :length="pageSize"
          :total-count="total"
          @paginate="paginate"
        ></pagination>
      </div>
    </div>

    <!-- Create carry forward modal -->
    <a-modal
      v-model="createVisible"
      :title="$t('结转损益')"
      :width="800"
      @cancel="handleCreateCancel"
      @ok="handleCreateSave"
      :confirmLoading="createLoading"
      destroyOnClose
    >
      <a-form-model :model="form" :rules="createRules" ref="createFormRef" layout="vertical">
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-model-item :label="$t('账簿')" prop="accountSetUnionCode">
              <SelectAccountSet
                :placeholder="$t('请选择账簿')"
                :name.sync="form.accountSetName"
                :code="form.accountSetUnionCode"
                @change="handleAccountSetChange"
              />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('名称')" prop="name">
              <a-input v-model="form.name" :placeholder="$t('名称')" />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('结算组织')" prop="businessUnitCode">
              <CommonSelect
                :isBu="true"
                :list="$store.state.settlementBuList"
                :placeholder="$t('结算组织')"
                :code.sync="form.businessUnitCode"
                :name.sync="form.businessUnitName"
              />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('本年利润科目')" prop="profitAccountCode">
              <a-input
                readOnly
                :value="
                  form.profitAccountCode
                    ? `${form.profitAccountCode}, ${form.profitAccountName || ''}`
                    : ''
                "
                @click="openProfitAccountModal"
                :placeholder="$t('选择科目')"
              >
                <a-icon slot="suffix" type="search" />
              </a-input>
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('凭证字')" prop="voucherType">
              <a-select v-model="form.voucherType" :placeholder="$t('请选择凭证字')">
                <a-select-option
                  v-for="item in journal_voucher_type"
                  :key="item.code"
                  :value="item.code"
                >
                  {{ item.name }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('凭证摘要')" prop="voucherSummary">
              <a-input v-model="form.voucherSummary" :placeholder="$t('请输入凭证摘要')" />
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="24">
            <a-form-model-item :label="$t('结转科目')" prop="specifiedAccountCodeList">
              <a-input
                readOnly
                :disabled="form.specifiedAccountCarryForward !== 1"
                :value="specifiedAccountsDisplay"
                @click="openSpecifiedAccountsModal"
                :placeholder="
                  form.specifiedAccountCarryForward === 1
                    ? $t('选择结转科目')
                    : $t('请先开启指定科目结转')
                "
              >
                <a-icon slot="suffix" type="search" />
              </a-input>
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-model-item :label="$t('指定科目结转')" prop="specifiedAccountCarryForward">
              <a-switch
                :checked="form.specifiedAccountCarryForward === 1"
                @change="(val) => handleSpecifiedAccountCarryForwardChange(val)"
              />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('收益和损失分别生成')" prop="incomeLossSeparateVoucher">
              <a-switch
                :checked="form.incomeLossSeparateVoucher === 1"
                @change="(val) => (form.incomeLossSeparateVoucher = val ? 1 : 0)"
              />
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-model-item :label="$t('本年利润按本位币结转')" prop="localCurrencyCarryForward">
              <a-switch
                :checked="form.localCurrencyCarryForward === 1"
                @change="(val) => (form.localCurrencyCarryForward = val ? 1 : 0)"
              />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('按余额反方向结转')" prop="reverseByBalanceDirection">
              <a-switch
                :checked="form.reverseByBalanceDirection === 1"
                @change="(val) => (form.reverseByBalanceDirection = val ? 1 : 0)"
              />
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-model-item :label="$t('未过账凭证处理方式')" prop="unpostedVoucherHandleType">
              <a-select
                v-model="form.unpostedVoucherHandleType"
                :placeholder="$t('请选择未过账凭证处理方式')"
              >
                <a-select-option
                  v-for="item in unposted_voucher_handle_type"
                  :key="item.code"
                  :value="item.code"
                >
                  {{ item.name }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('会计期间')" prop="accountingPeriod">
              <CommonQuerySelect
                api="queryAccountingCalendarDetailPage"
                valueName="accounting_period_code"
                labelName="accounting_period_name"
                :code.sync="form.accountingPeriod"
                :placeholder="$t('请选择会计期间')"
              />
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-model-item :label="$t('记账日期')" prop="accountingDate">
              <DatePicker
                :needhms="false"
                style="background-color: #ffffff; width: 100%"
                :init-date-time="form.accountingDate"
                @choose="chooseAccountingDate"
                :placehold="$t('请选择记账日期')"
              />
            </a-form-model-item>
          </a-col>
        </a-row>
      </a-form-model>
    </a-modal>

    <!-- Selection modals -->
    <selectAccountModal
      ref="selectProfitAccountModalRef"
      :single="true"
      :hasSelected="profitAccountObjects"
      @ok="handleProfitAccountSelected"
    />

    <selectAccountModal
      ref="selectSpecifiedAccountsModalRef"
      :single="false"
      :hasSelected="specifiedAccountObjects"
      @ok="handleSpecifiedAccountsSelected"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import http from '@/service/axios'
import api from '@/service/api'
import SelectAccountSet from '@/component/selectAccountSet'
import CommonQuerySelect from '@/component/commonQuerySelect'
import CommonTable from '@/component/commonTable'
import pagination from '@/component/pagination.vue'
import selectAccountModal from '@/component/selectAccountModal.vue'
import DatePicker from '@/component/datePicker'
import { convertKeysToCamelCase } from '@/common'

export default {
  name: 'ProfitLossCarryForwardList',
  components: {
    SelectAccountSet,
    CommonQuerySelect,
    CommonTable,
    pagination,
    selectAccountModal,
    DatePicker,
  },
  data() {
    return {
      loading: false,
      tableData: [],
      pageNo: 1,
      pageSize: 20,
      total: 0,
      columns: [],
      activeStatusKey: 'ALL',

      searchForm: {
        accountSetUnionCode: undefined,
        accountSetCode: undefined,
        businessUnitCode: undefined,
        accountingPeriod: undefined,
        orderSn: '',
        status: undefined,
      },

      createVisible: false,
      createLoading: false,
      form: {
        accountSetUnionCode: undefined,
        accountSetCode: '',
        accountSetName: '',
        businessUnitCode: '',
        businessUnitName: '',
        profitAccountCode: '',
        profitAccountName: '',
        voucherType: undefined,
        voucherSummary: '结转损益',
        specifiedAccountCarryForward: 0,
        specifiedAccountCodeList: [],
        incomeLossSeparateVoucher: 0,
        localCurrencyCarryForward: 0,
        reverseByBalanceDirection: 0,
        unpostedVoucherHandleType: 'TERMINATE',
        accountingPeriod: '',
        accountingDate: '',
      },
      profitAccountObjects: [],
      specifiedAccountObjects: [],
    }
  },
  mounted() {
    this.setColumns()
  },
  activated() {
    this.queryList()
  },
  computed: {
    ...mapState([
      'journal_voucher_type',
      'unposted_voucher_handle_type',
      'profit_loss_carry_forward_status',
    ]),
    ...mapGetters(['journalVoucherTypeMapping', 'profitLossCarryForwardStatusMapping']),
    statusTabList() {
      return this.profit_loss_carry_forward_status || []
    },
    specifiedAccountsDisplay() {
      if (!this.specifiedAccountObjects || this.specifiedAccountObjects.length === 0) {
        return ''
      }
      return this.specifiedAccountObjects
        .map((item) => `${item.code},${item.full_name || item.name || ''}`)
        .join('; ')
    },
    createRules() {
      const validateSpecifiedAccounts = (rule, value, callback) => {
        if (this.form.specifiedAccountCarryForward === 1 && (!value || value.length === 0)) {
          callback(new Error(this.$t('请选择结转科目')))
        } else {
          callback()
        }
      }
      return {
        accountSetUnionCode: [
          { required: true, message: this.$t('请选择账簿'), trigger: 'change' },
        ],
        businessUnitCode: [
          { required: true, message: this.$t('结算组织不能为空'), trigger: 'change' },
        ],
        name: [{ required: true, message: this.$t('名称不能为空'), trigger: 'change' }],
        profitAccountCode: [
          { required: true, message: this.$t('请选择本年利润科目'), trigger: 'change' },
        ],
        voucherType: [{ required: true, message: this.$t('请选择凭证字'), trigger: 'change' }],
        unpostedVoucherHandleType: [
          { required: true, message: this.$t('请选择未过账凭证处理方式'), trigger: 'change' },
        ],
        accountingPeriod: [
          { required: true, message: this.$t('请选择会计期间'), trigger: 'change' },
        ],
        accountingDate: [{ required: true, message: this.$t('请选择记账日期'), trigger: 'change' }],
        specifiedAccountCodeList: [{ validator: validateSpecifiedAccounts, trigger: 'change' }],
      }
    },
    detailColumns() {
      return [
        { title: this.$t('科目编码'), dataIndex: 'accountCode', align: 'left', width: 120 },
        { title: this.$t('科目全称'), dataIndex: 'accountFullName', align: 'left', width: 220 },
        { title: this.$t('科目名称'), dataIndex: 'accountName', align: 'left', width: 180 },
        {
          title: this.$t('余额方向'),
          dataIndex: 'balanceDirection',
          align: 'center',
          width: 90,
          customRender: (text) =>
            text === 'DEBIT' ? this.$t('借') : text === 'CREDIT' ? this.$t('贷') : text,
        },
        {
          title: this.$t('余额'),
          dataIndex: 'balance',
          align: 'right',
          width: 120,
          customRender: (text) => this.$currencySymbol(text),
        },
        {
          title: this.$t('借方金额'),
          dataIndex: 'debitAmount',
          align: 'right',
          width: 120,
          customRender: (text) => this.$currencySymbol(text),
        },
        {
          title: this.$t('贷方金额'),
          dataIndex: 'creditAmount',
          align: 'right',
          width: 120,
          customRender: (text) => this.$currencySymbol(text),
        },
        {
          title: this.$t('结转金额'),
          dataIndex: 'carryForwardAmount',
          align: 'right',
          width: 120,
          customRender: (text) => this.$currencySymbol(text),
        },
      ]
    },
  },
  methods: {
    ...mapMutations(['addPane']),
    setColumns() {
      this.columns = [
        {
          title: '#',
          align: 'center',
          key: 'NO',
          width: 60,
          scopedSlots: { customRender: 'no' },
        },
        {
          title: this.$t('单据编号'),
          dataIndex: 'orderSn',
          width: 200,
        },
        {
          title: this.$t('账簿名称'),
          dataIndex: 'accountSetName',
          width: 200,
        },
        {
          title: this.$t('业务单元名称'),
          dataIndex: 'businessUnitName',
          width: 180,
        },
        {
          title: this.$t('会计期间'),
          dataIndex: 'accountingPeriod',
          width: 100,
          align: 'center',
        },
        {
          title: this.$t('记账日期'),
          dataIndex: 'accountingDate',
          width: 120,
          align: 'center',
          customRender: (text) => text?.split(' ')?.[0],
        },
        {
          title: this.$t('本年利润科目'),
          dataIndex: 'profitAccountName',
          width: 180,
          customRender: (text, record) =>
            record.profitAccountCode
              ? `${record.profitAccountName}(${record.profitAccountCode})`
              : text,
        },
        {
          title: this.$t('指定科目结转'),
          dataIndex: 'specifiedAccountCarryForward',
          width: 110,
          align: 'center',
          scopedSlots: { customRender: 'specifiedAccountCarryForward' },
        },
        {
          title: this.$t('凭证字'),
          dataIndex: 'voucherType',
          width: 90,
          align: 'center',
          customRender: (text) => this.journalVoucherTypeMapping[text],
        },
        {
          title: this.$t('凭证号'),
          dataIndex: 'voucherSn',
          width: 180,
          scopedSlots: { customRender: 'voucherSn' },
        },
        {
          title: this.$t('反结转单号'),
          dataIndex: 'reverseVoucherSn',
          width: 180,
          scopedSlots: { customRender: 'reverseVoucherSn' },
        },
        {
          title: this.$t('状态'),
          dataIndex: 'status',
          width: 120,
          align: 'center',
          customRender: (text) => this.profitLossCarryForwardStatusMapping[text] || text || '-',
        },
        {
          title: this.$t('备注'),
          dataIndex: 'remark',
          width: 180,
        },
        {
          title: this.$t('操作'),
          key: 'action',
          width: 120,
          align: 'center',
          fixed: 'right',
          scopedSlots: { customRender: 'action' },
        },
      ]
    },
    changeSearchAccountSet(value) {
      if (value) {
        let arr = value.unionCode?.split('-')
        this.searchForm.accountSetUnionCode = value.unionCode
        this.searchForm.accountSetCode = arr ? arr[0] : undefined
        this.searchForm.businessUnitCode = value.business_unit_code
      } else {
        this.searchForm.accountSetUnionCode = undefined
        this.searchForm.accountSetCode = undefined
        this.searchForm.businessUnitCode = undefined
      }
    },
    resetSearch() {
      this.searchForm = {
        accountSetUnionCode: undefined,
        accountSetCode: undefined,
        businessUnitCode: undefined,
        accountingPeriod: undefined,
        orderSn: '',
        status: undefined,
      }
      this.activeStatusKey = 'ALL'
      this.pageNo = 1
      this.queryList()
    },
    handleStatusTabChange(key) {
      this.activeStatusKey = key
      this.searchForm.status = key === 'ALL' ? undefined : key
      this.queryList(1)
    },
    paginate(data) {
      this.pageNo = data.pageNo
      this.pageSize = data.pageSize
      this.queryList()
    },
    async queryList(pageNo = 0) {
      if (pageNo) {
        this.pageNo = pageNo
      }
      this.loading = true
      try {
        const res = await http({
          url: api.getProfitLossCarryForwardList,
          data: {
            pageNo: this.pageNo,
            pageSize: this.pageSize,
            accountSetCode: this.searchForm.accountSetCode,
            accountingPeriod: this.searchForm.accountingPeriod,
            businessUnitCode: this.searchForm.businessUnitCode,
            orderSn: this.searchForm.orderSn,
            status: this.searchForm.status,
          },
        })
        const result = convertKeysToCamelCase(res.result)
        this.tableData = result.list || []
        this.total = result.total || 0
      } catch (err) {
        this.$message.error(err?.msg || this.$t('获取列表失败'))
      } finally {
        this.loading = false
      }
    },
    viewVoucherDetail(voucherId, voucherSn) {
      this.addPane({
        view: 'voucherManage/voucherDetail/' + voucherSn,
        name: 'detailSn',
        query: {
          id: voucherId,
          sn: voucherSn,
        },
        route: `/voucherManage/voucherDetail?id=${voucherId}&sn=${voucherSn}`,
      })
    },
    async handleGenerateVoucher(record) {
      try {
        const res = await http({
          url: api.generateVoucherProfitLossCarryForward,
          data: { id: record.id },
          hasLoading: true,
        })
        if (res.success) {
          this.$message.success(this.$t('凭证生成成功'))
          this.queryList()
        } else {
          this.$message.error(res.msg || this.$t('凭证生成失败'))
        }
      } catch (err) {
        this.$message.error(err?.msg || this.$t('凭证生成失败'))
      }
    },
    confirmReverseVoucher(record) {
      this.$confirm({
        title: this.$t('确认操作'),
        content: `${this.$t('是否确认反结转单据')} ${record.orderSn || ''}？`,
        okText: this.$t('确定'),
        cancelText: this.$t('取消'),
        onOk: () => this.handleReverseVoucher(record),
      })
    },
    async handleReverseVoucher(record) {
      try {
        const res = await http({
          url: api.reverseVoucherProfitLossCarryForward,
          data: { id: record.id },
          hasLoading: true,
        })
        if (res.success) {
          this.$message.success(this.$t('反结转成功'))
          this.queryList()
        } else {
          this.$message.error(res.msg || this.$t('反结转失败'))
        }
      } catch (err) {
        this.$message.error(err?.msg || this.$t('反结转失败'))
      }
    },
    openCreateModal() {
      this.resetForm()
      this.createVisible = true
      setTimeout(() => {
        this.$refs.createFormRef?.clearValidate()
      }, 300)
    },
    resetForm() {
      this.form = {
        accountSetUnionCode: undefined,
        accountSetCode: '',
        accountSetName: '',
        businessUnitCode: '',
        businessUnitName: '',
        profitAccountCode: '',
        profitAccountName: '',
        voucherType: undefined,
        voucherSummary: '结转损益',
        specifiedAccountCarryForward: 0,
        specifiedAccountCodeList: [],
        incomeLossSeparateVoucher: 0,
        localCurrencyCarryForward: 0,
        reverseByBalanceDirection: 0,
        unpostedVoucherHandleType: 'TERMINATE',
        accountingPeriod: '',
        accountingDate: '',
      }
      this.profitAccountObjects = []
      this.specifiedAccountObjects = []
    },
    handleCreateCancel() {
      this.createVisible = false
    },
    handleAccountSetChange(item) {
      if (item) {
        this.form.accountSetUnionCode = item.unionCode
        let arr = item.unionCode?.split('-')
        this.form.accountSetCode = arr ? arr[0] : ''
        this.form.businessUnitCode = item.business_unit_code
        this.form.businessUnitName = item.business_unit_name
      } else {
        this.form.accountSetUnionCode = undefined
        this.form.accountSetCode = ''
        this.form.businessUnitCode = ''
        this.form.businessUnitName = ''
        this.form.accountingPeriod = ''
        this.form.accountingDate = ''
      }
    },
    chooseAccountingDate(data) {
      this.form.accountingDate = data.data
    },
    async getCurrentAccountingPeriod(buCode) {
      if (!buCode) return
      try {
        const res = await http({
          url: api.getCurrentAccountingPeriod,
          data: { ownerBusinessUnitCode: buCode },
        })
        if (res && res.result) {
          this.form.accountingPeriod = res.result.accounting_period_code
          this.form.accountingDate = res.result.end_date
        }
      } catch (err) {
        console.error('Failed to query current period', err)
      }
    },
    openProfitAccountModal() {
      this.$refs.selectProfitAccountModalRef.open()
    },
    handleProfitAccountSelected(selected) {
      if (selected && selected.length > 0) {
        this.form.profitAccountCode = selected[0].code
        this.form.profitAccountName = selected[0].full_name || selected[0].name
        this.profitAccountObjects = [selected[0]]
      } else {
        this.form.profitAccountCode = ''
        this.form.profitAccountName = ''
        this.profitAccountObjects = []
      }
    },
    handleSpecifiedAccountCarryForwardChange(val) {
      this.form.specifiedAccountCarryForward = val ? 1 : 0
      if (!val) {
        this.form.specifiedAccountCodeList = []
        this.specifiedAccountObjects = []
      }
    },
    openSpecifiedAccountsModal() {
      if (this.form.specifiedAccountCarryForward !== 1) return
      this.$refs.selectSpecifiedAccountsModalRef.open()
    },
    handleSpecifiedAccountsSelected(selected) {
      if (selected && selected.length > 0) {
        this.form.specifiedAccountCodeList = selected.map((item) => item.code)
        this.specifiedAccountObjects = selected
      } else {
        this.form.specifiedAccountCodeList = []
        this.specifiedAccountObjects = []
      }
    },
    handleCreateSave() {
      this.$refs.createFormRef.validate(async (valid) => {
        if (!valid) return
        this.createLoading = true
        try {
          const res = await http({
            url: api.createProfitLossCarryForward,
            data: {
              accountSetCode: this.form.accountSetCode,
              accountSetName: this.form.accountSetName,
              accountingDate: this.form.accountingDate,
              accountingPeriod: this.form.accountingPeriod,
              businessUnitCode: this.form.businessUnitCode,
              businessUnitName: this.form.businessUnitName,
              incomeLossSeparateVoucher: this.form.incomeLossSeparateVoucher,
              localCurrencyCarryForward: this.form.localCurrencyCarryForward,
              name: this.$t('结转损益单'),
              profitAccountCode: this.form.profitAccountCode,
              remark: '',
              reverseByBalanceDirection: this.form.reverseByBalanceDirection,
              specifiedAccountCarryForward: this.form.specifiedAccountCarryForward,
              specifiedAccountCodeList: this.form.specifiedAccountCodeList,
              unpostedVoucherHandleType: this.form.unpostedVoucherHandleType,
              voucherSummary: this.form.voucherSummary,
              voucherType: this.form.voucherType,
            },
            type: 'post',
          })
          if (res.success) {
            this.$message.success(this.$t('创建成功'))
            this.createVisible = false
            this.queryList(1)
          } else {
            this.$message.error(res.msg || this.$t('创建失败'))
          }
        } catch (err) {
          this.$message.error(err || this.$t('创建失败'))
        } finally {
          this.createLoading = false
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.text-link {
  color: #1890ff;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}
.mt10 {
  margin-top: 10px;
}
</style>
